import {
  AppBar,
  Divider,
  Drawer,
  Grid,
  GridProps,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Box } from '@mui/system';
import React, { ReactNode, useEffect, useState } from 'react';

interface HeaderProps {
  menu: NavItemProps[];
}

export default function Header(params: HeaderProps) {
  const [scrolling, setScrolling] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolling(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box sx={{ py: { xs: 3, md: 5 }, mb: 4 }}>
      <AppBar
        elevation={scrolling ? 4 : 0}
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          mb: 50,
          py: { xs: 1, md: 2 },
          backgroundColor: scrolling ? 'white' : 'transparent',
          transition:
            'background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out, height 0.3s ease-in-out',
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Link href="/" underline="none">
              <Box
                component="img"
                src="/assets/images/DailyControl-logo-main-CMYK.svg"
                alt="DailyControl Logo"
                width="100%"
                sx={{ maxWidth: { xs: 150, sm: 225 } }}
              />
            </Link>
            <Box>
              <Stack
                direction={'row'}
                alignItems={'center'}
                sx={{
                  textTransform: 'capitalize',
                  display: { xs: 'none', md: 'flex' },
                  gap: '1rem',
                }}
              >
                {params.menu.map((nav, index) => {
                  return (
                    <React.Fragment key={index}>
                      {(index === params.menu?.length - 1 || index === 1) && (
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ fontSize: 2 }}
                        />
                      )}
                      <NavItem {...nav} />
                    </React.Fragment>
                  );
                })}
              </Stack>
              <IconButton
                aria-label="open navigation menu"
                onClick={() => setMobileMenuOpen(true)}
                sx={{ display: { xs: 'inline-flex', md: 'none' }, p: 1 }}
              >
                <MenuIcon color="primary" />
              </IconButton>
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>
      <MobileMenu
        menu={params.menu}
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </Box>
  );
}

interface MobileMenuProps {
  menu: NavItemProps[];
  open: boolean;
  onClose: () => void;
}

function MobileMenu({ menu, open, onClose }: MobileMenuProps) {
  const renderItem = (item: NavItemProps, key: React.Key) => (
    <ListItemButton
      key={key}
      disabled={item.disabled}
      onClick={() => {
        item.action && item.action();
        onClose();
      }}
    >
      {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
      <ListItemText disableTypography primary={item.navtitle} />
    </ListItemButton>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ display: { xs: 'block', md: 'none' } }}
      PaperProps={{ sx: { width: '80%', maxWidth: 320, pt: 2 } }}
    >
      <List>
        {menu.map((item, index) => {
          if (item.submenu) {
            return (
              <React.Fragment key={index}>
                <Divider sx={{ my: 1 }} />
                {item.submenu.map((sub, subIndex) =>
                  sub === 'divider' ? (
                    <Divider key={`${index}-${subIndex}`} />
                  ) : (
                    renderItem(sub, `${index}-${subIndex}`)
                  )
                )}
              </React.Fragment>
            );
          }
          return renderItem(item, index);
        })}
      </List>
    </Drawer>
  );
}

export interface NavItemProps extends GridProps {
  navtitle: ReactNode;
  icon?: ReactNode;
  action?: () => void;
  submenu?: Array<NavItemProps | 'divider'>;
  disabled?: boolean;
}

function NavItem(props: NavItemProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    action?: () => void
  ) => {
    if (props.submenu) {
      setAnchorEl(event.currentTarget);
    } else if (action) {
      action();
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Grid {...props}>
      <Grid
        item
        container
        component={Link}
        onClick={(e: React.MouseEvent<HTMLElement>) =>
          handleClick(e, props.action)
        }
        underline="none"
        sx={{
          cursor: 'pointer',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '0.25rem',
        }}
      >
        <Grid item xs md={12}>
          {props.icon}
        </Grid>
        <Grid item xs>
          {props.navtitle}
        </Grid>
      </Grid>

      {props.submenu && (
        <Menu
          disableAutoFocusItem
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          slotProps={{
            paper: {
              elevation: 3,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 35,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {props.submenu.map((menu, index) => {
            if (menu === 'divider') {
              return <Divider key={index} />;
            }
            return (
              <MenuItem
                key={index}
                selected={false}
                disabled={menu.disabled}
                sx={{ pointerEvents: menu.action ? 'auto' : 'none' }}
                onClick={() => {
                  menu.action && menu.action();
                  handleClose();
                }}
              >
                {menu.icon && <ListItemIcon> {menu.icon} </ListItemIcon>}
                {menu.navtitle}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </Grid>
  );
}
